import { machineTypes, machineStates } from './inventoryMachines';
import { inventoryAccount } from './inventoryAccount';

const networkTypes = {
  AWS: [
    ['Networking (VPC)', 'VPC'], ['Networking (VPCs)', 'VPC'],
    ['Networking (Subnets)', 'subredes'], ['Networking (Network Interfaces)', 'interfaces de red'],
    ['Networking (Route Tables)', 'tablas de rutas'], ['Networking (Internet Gateways)', 'Internet Gateways'],
    ['Networking (NAT Gateways)', 'NAT Gateways'], ['Networking (Elastic IPs)', 'IP elásticas'],
    ['Networking (Load Balancers)', 'balanceadores'], ['Security (Security Groups)', 'grupos de seguridad'],
  ],
  AZURE: [
    ['Networking (Virtual Networks)', 'redes virtuales'], ['Networking (Network Interfaces)', 'interfaces de red'],
    ['Networking (Public IPs)', 'IP públicas'], ['Networking (Route Tables)', 'tablas de rutas'],
    ['Networking (Load Balancers)', 'balanceadores'], ['Networking (NAT Gateways)', 'NAT Gateways'],
    ['Networking (App Gateways)', 'Application Gateways'], ['Networking (VPN Gateways)', 'VPN Gateways'],
    ['Networking (Private Endpoints)', 'endpoints privados'],
    ['Networking (Network Security Groups)', 'grupos de seguridad de red'],
  ],
  GCP: [
    ['Networking (VPC Networks)', 'redes VPC'], ['Networking (Subnets)', 'subredes'],
    ['Networking (External IPs)', 'direcciones IP externas'], ['Networking (Forwarding Rules)', 'reglas de reenvío'],
    ['Networking (VPN Tunnels)', 'túneles VPN'], ['Networking (Interconnects)', 'interconexiones'],
    ['Networking (VPC Peerings)', 'peerings VPC'], ['Networking (Routes)', 'rutas'],
    ['Security (Firewall Rules)', 'reglas de firewall'],
  ],
  HUAWEI: [
    ['Networking (VPC)', 'VPC'], ['Networking (Elastic IPs)', 'IP elásticas'],
    ['Networking (Load Balancers)', 'balanceadores'], ['Networking (DNS Public Zones)', 'zonas DNS públicas'],
  ],
  OCI: [
    ['Networking (OCI VCNs)', 'redes VCN'], ['Networking (OCI Subnets)', 'subredes'],
    ['Networking (OCI Load Balancers)', 'balanceadores'],
    ['Networking (OCI Network Load Balancers)', 'balanceadores de red'],
  ],
};

function commitmentSummary(detail) {
  const contracts = detail.por_servicio['Billing (Commitments)'];
  const coverage = contracts?.coverage;
  if (!coverage?.length) return 'Compromisos y descuentos contratados: no verificados en esta captura; no significa cero contratos.';
  const verified = coverage.filter(item => item.status === 'ok');
  const sections = ['Compromisos y descuentos contratados (no son VM ni reservas de capacidad):'];
  if (verified.length) {
    sections.push(`${contracts.total_real} registros contractuales en los alcances verificados, incluyendo los estados devueltos por el proveedor.`);
    sections.push([...new Set(verified.map(item => item.product))].join('; ') + '.');
    if (contracts.contract_states) sections.push('Estados: ' + Object.entries(contracts.contract_states)
      .map(([state, count]) => `${state}: ${count}`).join(', ') + '.');
    const scopes = [...new Set(verified.map(item => item.scope).filter(Boolean))];
    if (scopes.length) sections.push(`Alcance contractual: ${scopes.join('; ')}.`);
  }
  const unverified = coverage.length - verified.length;
  if (unverified) sections.push(`${unverified} alcance(s) no verificado(s) por falta de recolección o error; no se interpreta como cero contratos ni como total completo.`);
  return sections.join(' ');
}

export function buildInventorySummary(inventory, detail, formattedDate) {
  const client = inventory.nombre_cliente || inventory.nombre_cliente_legacy
    || detail.metadata.nombre_cliente || 'el cliente seleccionado';
  const provider = inventory.proveedor;
  const total = detail.metadata.total_recursos;
  const groups = Object.entries(detail.por_servicio)
    .map(([name, group]) => ({ name, total: group.total_real ?? group.items?.length ?? 0 }))
    .filter((group) => group.total > 0)
    .sort((a, b) => b.total - a.total || a.name.localeCompare(b.name));
  const account = inventoryAccount(inventory);
  const capture = `La captura de ${client} en ${provider}${account ? ` (${account})` : ''} corresponde al ${formattedDate}, hora de Ciudad de México.`;
  const coverage = detail.por_servicio['Collection (OCI Coverage)']?.coverage;
  const failed = coverage?.filter(item => item.status === 'error').length;
  const scopeNotice = coverage
    ? ` OCI: ${coverage.length - failed} de ${coverage.length} consultas completadas en los tipos, regiones y compartimentos configurados.${failed ? ` ${failed} consultas fallaron; los conteos son parciales, no totales completos.` : ''}`
    : '';
  if (total === 0) {
    return `${capture}${scopeNotice} No se identificaron recursos con los tipos y filtros que reconoce el lector de inventarios. ${commitmentSummary(detail)}`;
  }
  const count = `${total.toLocaleString('es-MX')} ${total === 1 ? 'recurso identificado' : 'recursos identificados'}`;
  const types = `${groups.length} ${groups.length === 1 ? 'tipo de recurso' : 'tipos de recurso'}`;
  const leading = groups.slice(0, 3)
    .map((group) => `${group.name}: ${group.total.toLocaleString('es-MX')}`).join('; ');
  const sections = [`${capture}${scopeNotice} Contiene ${count}${groups.length ? ` en ${types}` : ''}.`];
  const cloud = provider?.toUpperCase();
  const regions = detail.metadata.regiones || [];
  if (regions.length) sections.push(`Regiones incluidas: ${regions.join(', ')}.`);
  if (cloud === 'AZURE') {
    const arc = [
      ['Compute (Azure Arc Servers)', 'servidor conectado a Azure Arc', 'servidores conectados a Azure Arc'],
      ['Database (Azure Arc SQL Servers)', 'instancia SQL Server habilitada por Azure Arc', 'instancias SQL Server habilitadas por Azure Arc'],
      ['Database (Azure Arc SQL Databases)', 'base de datos SQL de Azure Arc', 'bases de datos SQL de Azure Arc'],
      ['Management (Azure Arc Extensions)', 'extensión de Azure Arc', 'extensiones de Azure Arc'],
    ].flatMap(([type, singular, plural]) => {
      const count = detail.por_servicio[type]?.total_real ?? 0;
      return count > 0 ? [`${count.toLocaleString('es-MX')} ${count === 1 ? singular : plural}`] : [];
    });
    if (arc.length) sections.push(`Azure Arc: ${arc.join('; ')}. Son recursos registrados en Arc; no se cuentan como máquinas virtuales nativas de Azure ni demuestran su estado actual.`);
  }
  const machineType = machineTypes[cloud];
  if (machineType) {
    const machines = detail.por_servicio[machineType];
    const label = { AWS: 'instancias EC2', AZURE: 'máquinas virtuales Azure', GCP: 'instancias Compute Engine', HUAWEI: 'servidores ECS', OCI: 'instancias Oracle Compute' }[cloud];
    if (machines) {
      const instanceCount = machines.total_real ?? machines.items?.length ?? 0;
      let sentence = `Se identificaron ${instanceCount.toLocaleString('es-MX')} ${label}`;
      if (machines.states) {
        const states = machines.states;
        const { on, off } = machineStates[cloud];
        const onCount = on.reduce((sum, state) => sum + (states[state] ?? 0), 0);
        const offCount = off.reduce((sum, state) => sum + (states[state] ?? 0), 0);
        const other = Object.entries(states)
          .filter(([state]) => ![...on, ...off, 'unknown'].includes(state))
          .map(([state, value]) => `${value.toLocaleString('es-MX')} en estado ${state}`);
        sentence += cloud === 'AWS'
          ? `: ${onCount.toLocaleString('es-MX')} encendidas (running) y ${offCount.toLocaleString('es-MX')} apagadas (stopped)`
          : `: ${onCount.toLocaleString('es-MX')} encendidas y ${offCount.toLocaleString('es-MX')} no encendidas (detenidas, desasignadas o suspendidas según el proveedor)`;
        if (cloud !== 'AWS') {
          const reported = Object.entries(states)
            .filter(([state]) => [...on, ...off].includes(state))
            .map(([state, count]) => `${state}: ${count.toLocaleString('es-MX')}`);
          if (reported.length) sentence += `; desglose: ${reported.join(', ')}`;
        }
        if (other.length) sentence += `; ${other.join('; ')}`;
        if (states.unknown) sentence += `; ${states.unknown.toLocaleString('es-MX')} sin estado informado`;
        sentence += ', según la captura';
      } else {
        sentence += '; el desglose de estados no está disponible en esta respuesta';
      }
      sections.push(`${sentence}.`);
      if (machines.sizes) {
        const sizes = Object.entries(machines.sizes)
          .filter(([size]) => size !== 'unknown')
          .sort((a, b) => b[1] - a[1] || a[0].localeCompare(b[0]));
        if (sizes.length) {
          sections.push(`Tamaños / SKU más frecuentes: ${sizes.slice(0, 5).map(([size, count]) => `${size}: ${count.toLocaleString('es-MX')}`).join('; ')}${sizes.length > 5 ? ` (5 de ${sizes.length} tamaños informados)` : ''}.`);
        }
        if (machines.sizes.unknown) sections.push(`${machines.sizes.unknown.toLocaleString('es-MX')} máquinas sin tamaño / SKU informado.`);
      }
    } else {
      sections.push(`No se identificaron ${label} entre los recursos reconocidos en esta captura.`);
    }
    const disks = Object.entries(detail.por_servicio).filter(([, group]) => group.capacity);
    for (const [name, group] of disks) {
      const { size_gib_total: size, with_size: known, without_size: unknown } = group.capacity;
      sections.push(`${name}: ${group.total_real.toLocaleString('es-MX')} discos reconocidos; ${size.toLocaleString('es-MX')} GiB aprovisionados en ${known.toLocaleString('es-MX')} discos con capacidad informada${unknown ? ` y ${unknown.toLocaleString('es-MX')} sin capacidad informada` : ''}.`);
    }
    if (disks.length) sections.push('La capacidad aprovisionada de discos no indica uso ni espacio libre dentro del sistema operativo; no incluye capacidad de objetos, snapshots ni almacenamiento efímero.');
    const networks = networkTypes[cloud].flatMap(([type, label]) => {
      const group = detail.por_servicio[type];
      return group?.total_real > 0 ? [`${group.total_real.toLocaleString('es-MX')} ${label}`] : [];
    });
    if (networks.length) sections.push(`En redes y conectividad se identificaron ${networks.join(', ')}.`);
  }
  if (leading) sections.push(`Los tipos con mayor presencia son ${leading}.`);
  sections.push(commitmentSummary(detail));
  return sections.join(' ');
}
